"use client";

import { useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { RefreshCw, Home, MessageCircle } from "lucide-react";

export default function RootError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("Root error:", error);
    try {
      // Capture as much context as possible — empty error.message is a
      // common React-prod pattern (hydration mismatch, dynamic-import
      // rejection, throw-non-Error) and was previously unactionable.
      // error.name + toString + digest at least lets us fingerprint.
      const messageText = error.message || error.name || String(error) || "(empty error)";
      fetch("/api/errors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: `Root error [${error.name || "Error"}]: ${messageText}`,
          stack: error.stack,
          digest: error.digest,
          url: typeof window !== "undefined" ? window.location.href : "",
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
          context: {
            kind: "root-error-boundary",
            errorName: error.name,
            errorDigest: error.digest,
            errorString: String(error),
            hasMessage: !!error.message,
          },
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {}
  }, [error]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-white px-6 py-20 text-center">
      {/* Minimal header */}
      <Link href="/" className="absolute top-5 start-6 hover:opacity-80 transition-opacity">
        <Image src="/images/logo.png?v=2" alt="Rivky Wigs" width={120} height={60} className="h-10 w-auto" />
      </Link>

      {/* Decorative accent */}
      <div className="mb-10 h-px w-16 bg-brand-gold" />

      {/* Icon */}
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-brand-cream">
        <div className="h-8 w-8 rounded-full border-2 border-brand-gold/30 flex items-center justify-center">
          <span className="text-brand-gold text-lg font-display">!</span>
        </div>
      </div>

      <h1 className="font-display text-2xl tracking-tight text-brand-dark sm:text-3xl">
        Something Went Wrong
      </h1>

      <p className="mt-4 max-w-md text-sm leading-relaxed text-brand-muted">
        We&apos;re sorry for the inconvenience. Our team has been notified and is working on it.
      </p>

      {/* Actions */}
      <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:gap-4">
        <button
          onClick={reset}
          className="inline-flex items-center gap-2.5 bg-brand-dark px-8 py-3.5 text-xs font-medium uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-brand-gold active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/40 focus-visible:ring-offset-2"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Try Again
        </button>

        <Link
          href="/"
          className="inline-flex items-center gap-2.5 border border-brand-dark px-8 py-3.5 text-xs font-medium uppercase tracking-[0.15em] text-brand-dark transition-all duration-300 hover:bg-brand-dark hover:text-white active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/40 focus-visible:ring-offset-2"
        >
          <Home className="h-3.5 w-3.5" />
          Go to Homepage
        </Link>
      </div>

      {/* Contact support */}
      <div className="mt-12 max-w-sm rounded-sm border border-brand-dark/5 bg-brand-cream/30 px-6 py-5">
        <p className="text-xs leading-relaxed text-brand-muted">
          If this keeps happening, please don&apos;t hesitate to reach out to our support team.
        </p>
        <Link
          href="/consultation"
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-brand-gold transition-colors duration-300 hover:text-brand-gold-dark"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          Contact Support
        </Link>
      </div>

      {/* Decorative accent */}
      <div className="mt-10 h-px w-16 bg-brand-gold" />
    </div>
  );
}
